import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Avatar,
  Box,
  Alert,
  Pagination,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Article as ArticleIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { supabaseService } from '../services/supabaseService';

// Constants
const POST_REPORTS_PER_PAGE = 20;

interface PostReport {
  id: string;
  reporter_user_id: string;
  reported_user_id: string;
  post_id: string;
  reason: string;
  description?: string;
  content_snapshot?: string;
  priority: number;
  moderator_id?: string;
  moderated_at?: string;
  report_status: 'pending' | 'under_review' | 'resolved' | 'dismissed';
  admin_notes?: string;
  created_at: string;
  updated_at: string;
  reporter?: {
    id: string;
    name: string;
    email: string;
    photos?: string[];
  };
  reported_user?: {
    id: string;
    name: string;
    email: string;
    photos?: string[];
  };
  moderator?: {
    id: string;
    name: string;
    email: string;
    photos?: string[];
  };
  post_content_snapshot?: string;
  post_image_snapshot?: string;
}

interface PostReportsStatistics {
  total: number;
  pending: number;
  under_review: number;
  resolved: number;
  dismissed: number;
}

const PostReportsPage: React.FC = () => {
  const [postReports, setPostReports] = useState<PostReport[]>([]);
  const [postStatistics, setPostStatistics] = useState<PostReportsStatistics>({
    total: 0,
    pending: 0,
    under_review: 0,
    resolved: 0,
    dismissed: 0,
  });
  const [postLoading, setPostLoading] = useState(true);
  const [selectedPostReport, setSelectedPostReport] = useState<PostReport | null>(null);
  const [postUpdateDialogOpen, setPostUpdateDialogOpen] = useState(false);
  const [postDetailDialogOpen, setPostDetailDialogOpen] = useState(false);
  const [newPostStatus, setNewPostStatus] = useState('');
  const [postAdminNotes, setPostAdminNotes] = useState('');
  const [postStatusFilter, setPostStatusFilter] = useState('');
  const [postPage, setPostPage] = useState(1);
  const [postError, setPostError] = useState<string | null>(null);

  const statusColors = {
    pending: 'warning',
    under_review: 'info',
    resolved: 'success',
    dismissed: 'default',
  } as const;

  const statusLabels = {
    pending: 'Beklemede',
    under_review: 'İnceleniyor',
    resolved: 'Çözüldü',
    dismissed: 'Reddedildi',
  };

  // Post Reports Functions
  const loadPostReports = React.useCallback(async (currentPage = 1) => {
    console.log('📋 [DEBUG] Loading post reports...', {
      currentPage,
      limit: POST_REPORTS_PER_PAGE,
      offset: (currentPage - 1) * POST_REPORTS_PER_PAGE,
      postStatusFilter
    });
    console.log('📋 [DEBUG] Calling supabaseService.getAllPostReports...');
    
    try {
      setPostLoading(true);
      const offset = (currentPage - 1) * POST_REPORTS_PER_PAGE;
      const reports = await supabaseService.getAllPostReports({
        limit: POST_REPORTS_PER_PAGE,
        offset,
        statusFilter: postStatusFilter || undefined,
      });
      
      console.log('📊 [DEBUG] Post reports loaded from service:', reports);
      console.log('📊 [DEBUG] Reports data type:', typeof reports);
      console.log('📊 [DEBUG] Reports data length:', reports?.length);
      console.log('📊 [DEBUG] First report:', reports?.[0]);
      
      setPostReports(reports);
      console.log('📊 [DEBUG] State updated with reports');
    } catch (err: any) {
      console.error('❌ Error loading post reports:', err);
      setPostError(err.message || 'Post raporları yüklenirken hata oluştu');
    } finally {
      setPostLoading(false);
    }
  }, [postStatusFilter]);

  const loadPostStatistics = React.useCallback(async () => {
    try {
      const stats = await supabaseService.getPostReportsStatistics();
      console.log('📈 Post statistics loaded:', stats);
      setPostStatistics(stats);
    } catch (err: any) {
      console.error('❌ Error loading post statistics:', err);
    }
  }, []);

  useEffect(() => {
    loadPostReports(postPage);
    loadPostStatistics();
  }, [loadPostReports, loadPostStatistics, postPage]);

  const handlePostStatusUpdate = async () => {
    if (!selectedPostReport || !newPostStatus) return;

    try {
      setPostLoading(true);
      const success = await supabaseService.updatePostReportStatus(
        selectedPostReport.id,
        newPostStatus,
        postAdminNotes || undefined,
        'admin_user_id'
      );

      if (success) {
        setPostUpdateDialogOpen(false);
        setSelectedPostReport(null);
        setNewPostStatus('');
        setPostAdminNotes('');
        await loadPostReports(postPage);
        await loadPostStatistics();
      } else {
        setPostError('Post report durumu güncellenemedi');
      }
    } catch (err: any) {
      console.error('❌ Error updating post report:', err);
      setPostError(err.message || 'Güncelleme başarısız');
    } finally {
      setPostLoading(false);
    }
  };

  const handleDeletePostReport = async (reportId: string) => {
    if (!window.confirm('Bu post raporunu silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setPostLoading(true);
      const success = await supabaseService.deletePostReport(reportId);

      if (success) {
        await loadPostReports(postPage);
        await loadPostStatistics();
      } else {
        setPostError('Post report silinemedi');
      }
    } catch (err: any) {
      console.error('❌ Error deleting post report:', err);
      setPostError(err.message || 'Silme işlemi başarısız');
    } finally {
      setPostLoading(false);
    }
  };

  const handleDeleteReportedPost = async (report: PostReport) => {
    if (!window.confirm(`Bu postu kalıcı olarak silmek istediğinizden emin misiniz?\n\nPost ID: ${report.post_id}\nRapor eden: ${report.reporter?.name || 'Bilinmeyen'}\nSebep: ${report.reason}`)) {
      return;
    }

    try {
      setPostLoading(true);
      const success = await supabaseService.deleteReportedPost(report.post_id);

      if (success) {
        // Post silindikten sonra raporu da "resolved" olarak işaretle
        await supabaseService.updatePostReportStatus(
          report.id,
          'resolved',
          'Post admin tarafından silindi',
          'admin_user_id'
        );
        await loadPostReports(postPage);
        await loadPostStatistics();
        alert('Post başarıyla silindi!');
      } else {
        setPostError('Post silinemedi');
      }
    } catch (err: any) {
      console.error('❌ Error deleting reported post:', err);
      setPostError(err.message || 'Post silme işlemi başarısız');
    } finally {
      setPostLoading(false);
    }
  };

  const openPostUpdateDialog = (report: PostReport) => {
    setSelectedPostReport(report);
    setNewPostStatus(report.report_status);
    setPostAdminNotes(report.admin_notes || '');
    setPostUpdateDialogOpen(true);
  };

  const openPostDetailDialog = (report: PostReport) => {
    setSelectedPostReport(report);
    setPostDetailDialogOpen(true);
  };

  const getPhotoUrl = (photos?: string[]) => {
    return photos && photos.length > 0 ? photos[0] : '/default-avatar.png';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('tr-TR');
  };

  const getPriorityLabel = (priority: number) => {
    switch (priority) {
      case 1: return 'Düşük';
      case 2: return 'Orta';
      case 3: return 'Yüksek';
      case 4: return 'Kritik';
      default: return 'Belirsiz';
    }
  };

  const getPriorityColor = (priority: number) => {
    switch (priority) {
      case 1: return 'success';
      case 2: return 'info';
      case 3: return 'warning';
      case 4: return 'error';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center' }}>
          <ArticleIcon sx={{ mr: 2, color: 'primary.main' }} />
          📋 Post Raporları
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            loadPostReports(postPage);
            loadPostStatistics();
          }}
          disabled={postLoading}
        >
          Yenile
        </Button>
      </Box>

      {postError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setPostError(null)}>
          {postError}
        </Alert>
      )}

      {/* Post Reports Statistics */}
      <Box 
        sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: 3,
          mb: 4
        }}
      >
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography color="textSecondary" gutterBottom>
              Toplam Post Raporu
            </Typography>
            <Typography variant="h4" component="div">
              {postStatistics.total}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography color="textSecondary" gutterBottom>
              Beklemede
            </Typography>
            <Typography variant="h4" component="div" color="warning.main">
              {postStatistics.pending}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography color="textSecondary" gutterBottom>
              İnceleniyor
            </Typography>
            <Typography variant="h4" component="div" color="info.main">
              {postStatistics.under_review}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography color="textSecondary" gutterBottom>
              Çözüldü
            </Typography>
            <Typography variant="h4" component="div" color="success.main">
              {postStatistics.resolved}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography color="textSecondary" gutterBottom>
              Reddedildi
            </Typography>
            <Typography variant="h4" component="div" color="text.secondary">
              {postStatistics.dismissed}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Post Reports Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Status Filtresi</InputLabel>
            <Select
              value={postStatusFilter}
              onChange={(e) => {
                setPostStatusFilter(e.target.value);
                setPostPage(1);
              }}
              label="Status Filtresi"
            >
              <MenuItem value="">Tümü</MenuItem>
              <MenuItem value="pending">Beklemede</MenuItem>
              <MenuItem value="under_review">İnceleniyor</MenuItem>
              <MenuItem value="resolved">Çözüldü</MenuItem>
              <MenuItem value="dismissed">Reddedildi</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Post Reports Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Rapor Eden</TableCell>
              <TableCell>Post ID</TableCell>
              <TableCell>Post İçeriği</TableCell>
              <TableCell>Sebep</TableCell>
              <TableCell>Öncelik</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell>Tarih</TableCell>
              <TableCell align="center">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {postLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress size={40} />
                </TableCell>
              </TableRow>
            ) : postReports.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body1" color="textSecondary">
                    Hiç post raporu bulunamadı
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              postReports.map((report) => (
                <TableRow key={report.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        src={getPhotoUrl(report.reporter?.photos)}
                        sx={{ width: 32, height: 32, mr: 2 }}
                      />
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {report.reporter?.name || 'Unknown User'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {report.reporter?.email || 'No email'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {report.post_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ maxWidth: 200 }}>
                      {report.post_content_snapshot ? (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            fontSize: '0.875rem',
                            lineHeight: 1.2
                          }}
                        >
                          {report.post_content_snapshot}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary" fontStyle="italic">
                          İçerik mevcut değil
                        </Typography>
                      )}
                      {report.post_image_snapshot && (
                        <Chip 
                          label="📷 Görsel var" 
                          size="small" 
                          color="info"
                          sx={{ mt: 0.5, fontSize: '0.75rem' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 150 }}>
                      {report.reason}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getPriorityLabel(report.priority)}
                      color={getPriorityColor(report.priority)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusLabels[report.report_status]}
                      color={statusColors[report.report_status]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(report.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Detayları Görüntüle">
                      <IconButton
                        onClick={() => openPostDetailDialog(report)}
                        size="small"
                        color="primary"
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Durum Güncelle">
                      <IconButton
                        onClick={() => openPostUpdateDialog(report)}
                        size="small"
                        color="info"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Raporu Sil">
                      <IconButton
                        onClick={() => handleDeletePostReport(report.id)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="🗑️ Postu Sil">
                      <IconButton
                        onClick={() => handleDeleteReportedPost(report)}
                        size="small"
                        color="error"
                        sx={{ 
                          bgcolor: 'error.light', 
                          color: 'white',
                          '&:hover': { bgcolor: 'error.dark' }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Post Reports Pagination */}
      {postReports.length >= POST_REPORTS_PER_PAGE && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={10}
            page={postPage}
            onChange={(_, newPage) => setPostPage(newPage)}
            color="primary"
          />
        </Box>
      )}

      {/* Post Report Update Dialog */}
      <Dialog open={postUpdateDialogOpen} onClose={() => setPostUpdateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Post Raporu Durumunu Güncelle</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Yeni Durum</InputLabel>
            <Select
              value={newPostStatus}
              onChange={(e) => setNewPostStatus(e.target.value)}
              label="Yeni Durum"
            >
              <MenuItem value="pending">Beklemede</MenuItem>
              <MenuItem value="under_review">İnceleniyor</MenuItem>
              <MenuItem value="resolved">Çözüldü</MenuItem>
              <MenuItem value="dismissed">Reddedildi</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Admin Notları"
            value={postAdminNotes}
            onChange={(e) => setPostAdminNotes(e.target.value)}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostUpdateDialogOpen(false)}>İptal</Button>
          <Button onClick={handlePostStatusUpdate} variant="contained" disabled={postLoading}>
            {postLoading ? <CircularProgress size={20} /> : 'Güncelle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Post Report Detail Dialog */}
      <Dialog open={postDetailDialogOpen} onClose={() => setPostDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Post Raporu Detayları</DialogTitle>
        <DialogContent>
          {selectedPostReport && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Post Rapor Bilgileri
              </Typography>
              <Typography><strong>ID:</strong> {selectedPostReport.id}</Typography>
              <Typography><strong>Post ID:</strong> {selectedPostReport.post_id}</Typography>
              <Typography><strong>Sebep:</strong> {selectedPostReport.reason}</Typography>
              <Typography><strong>Açıklama:</strong> {selectedPostReport.description || 'Yok'}</Typography>
              <Typography><strong>Öncelik:</strong> 
                <Chip
                  label={getPriorityLabel(selectedPostReport.priority)}
                  color={getPriorityColor(selectedPostReport.priority)}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Typography>
              <Typography><strong>Durum:</strong> 
                <Chip
                  label={statusLabels[selectedPostReport.report_status]}
                  color={statusColors[selectedPostReport.report_status]}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Typography>
              <Typography><strong>Oluşturma Tarihi:</strong> {formatDate(selectedPostReport.created_at)}</Typography>
              <Typography><strong>Güncelleme Tarihi:</strong> {formatDate(selectedPostReport.updated_at)}</Typography>
              {selectedPostReport.moderated_at && (
                <Typography><strong>Moderasyon Tarihi:</strong> {formatDate(selectedPostReport.moderated_at)}</Typography>
              )}
              {selectedPostReport.admin_notes && (
                <Typography><strong>Admin Notları:</strong> {selectedPostReport.admin_notes}</Typography>
              )}
              {selectedPostReport.content_snapshot && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    İçerik Yedeği
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.100' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedPostReport.content_snapshot}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Rapor Edilen Post Bilgileri */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Rapor Edilen Post
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
                  <Typography><strong>Post ID:</strong> {selectedPostReport.post_id}</Typography>
                  {selectedPostReport.post_content_snapshot && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Post İçeriği:</Typography>
                      <Typography variant="body2" sx={{ 
                        whiteSpace: 'pre-wrap', 
                        bgcolor: 'white',
                        p: 2,
                        borderRadius: 1,
                        maxHeight: 200,
                        overflow: 'auto'
                      }}>
                        {selectedPostReport.post_content_snapshot}
                      </Typography>
                    </Box>
                  )}
                  {selectedPostReport.post_image_snapshot && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Post Görseli:</Typography>
                      <Box sx={{ 
                        maxWidth: 300, 
                        maxHeight: 200, 
                        overflow: 'hidden',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.300'
                      }}>
                        <img 
                          src={selectedPostReport.post_image_snapshot} 
                          alt="Post görseli"
                          style={{ 
                            width: '100%', 
                            height: 'auto',
                            display: 'block'
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </Paper>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Rapor Eden Kullanıcı
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    src={getPhotoUrl(selectedPostReport.reporter?.photos)}
                    sx={{ width: 64, height: 64, mr: 2 }}
                  />
                  <Box>
                    <Typography variant="h6">{selectedPostReport.reporter?.name || 'Unknown User'}</Typography>
                    <Typography color="textSecondary">{selectedPostReport.reporter?.email || 'No email'}</Typography>
                  </Box>
                </Box>
              </Box>

              {selectedPostReport.moderator && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Moderatör
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      src={getPhotoUrl(selectedPostReport.moderator?.photos)}
                      sx={{ width: 64, height: 64, mr: 2 }}
                    />
                    <Box>
                      <Typography variant="h6">{selectedPostReport.moderator?.name || 'Unknown User'}</Typography>
                      <Typography color="textSecondary">{selectedPostReport.moderator?.email || 'No email'}</Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPostDetailDialogOpen(false)}>Kapat</Button>
          <Button
            onClick={() => {
              setPostDetailDialogOpen(false);
              if (selectedPostReport) openPostUpdateDialog(selectedPostReport);
            }}
            variant="contained"
          >
            Düzenle
          </Button>
          <Button
            onClick={() => {
              if (selectedPostReport) {
                setPostDetailDialogOpen(false);
                handleDeleteReportedPost(selectedPostReport);
              }
            }}
            variant="contained"
            color="error"
            sx={{ ml: 1 }}
          >
            🗑️ Postu Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PostReportsPage; 